import React from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../styles/globals';

const ProgramContainer = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.darkBackground} 100%);
  color: ${colors.white};
  padding: 80px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.2;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const ProgramContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const ProgramTitle = styled.h2`
  font-family: ${fonts.playfair};
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  margin-bottom: 16px;
  line-height: 1.2;
  background: linear-gradient(135deg, ${colors.white} 0%, ${colors.accent} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ProgramSubtitle = styled.p`
  font-family: ${fonts.inter};
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 50px;
  line-height: 1.5;
  opacity: 0.95;
`;

const ProgramFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-bottom: 40px;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 30px;
  text-align: left;
  display: flex;
  align-items: flex-start;
  gap: 20px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateX(10px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 15px;
    padding: 25px 20px;

    &:hover {
      transform: translateY(-5px);
    }
  }
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${colors.accent} 0%, ${colors.secondary} 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 20px;
  color: ${colors.white};
  flex-shrink: 0;

  @media (max-width: 768px) {
    margin: 0 auto 0;
  }
`;

const FeatureTitle = styled.h3`
  font-family: ${fonts.playfair};
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 15px;
  line-height: 1.3;
`;

const FeatureDescription = styled.p`
  font-family: ${fonts.inter};
  font-size: 1rem;
  line-height: 1.6;
  opacity: 0.9;
`;

const ProgramNote = styled.div`
  background: rgba(243, 156, 107, 0.2);
  border: 1px solid rgba(243, 156, 107, 0.3);
  border-radius: 16px;
  padding: 25px;
  margin-top: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const NoteText = styled.p`
  font-family: ${fonts.playfair};
  font-size: 1.1rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${colors.accent};
`;

function ProgramSection() {
  const features = [
    {
      icon: '•',
      title: 'Sanación emocional',
      description: 'Trabajamos traumas, rechazo, pánico escénico y todas las emociones que te bloquean al hablar.'
    },
    {
      icon: '•',
      title: 'Técnicas de oratoria con presencia escénica',
      description: 'Aprendes a conectar con tu audiencia desde la autenticidad, no desde la actuación.'
    },
    {
      icon: '•',
      title: 'Ejercicios corporales, escritura terapéutica y voz consciente',
      description: 'Integramos cuerpo, mente y voz para una comunicación holística y transformadora.'
    },
    {
      icon: '•',
      title: 'Seguimiento diario + comunidad íntima y segura',
      description: 'No estás solo. Te acompañamos cada día en un espacio seguro para tu transformación.'
    },
    {
      icon: '•',
      title: 'Formato online en vivo',
      description: 'Sesiones interactivas donde puedes practicar y recibir feedback en tiempo real.'
    }
  ];

  return (
    <ProgramContainer>
      <ProgramContent>
        <ProgramTitle>El entrenamiento "Habla Desde Tu Poder"</ProgramTitle>
        <ProgramSubtitle>
          Una experiencia de 21 días que transforma tu relación con tu voz y contigo.
        </ProgramSubtitle>

        <ProgramFeatures>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <div>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </div>
            </FeatureCard>
          ))}
        </ProgramFeatures>

        <ProgramNote>
          <NoteText>
            No es teoría. Es transformación real con acompañamiento diario.
          </NoteText>
        </ProgramNote>
      </ProgramContent>
    </ProgramContainer>
  );
}

export default ProgramSection; 